import { z } from 'zod';

// ── Autocapture settings (per-project, versioned) ──────────
// The single source of truth for the shape of `projects.autocapture_settings`
// (jsonb). Validated by the backend on write and projected to the SDK on the
// track response. The schema is **versioned** so the future rich-capture phase
// (elements_chain, CSS selectors, frustration signals) can extend it WITHOUT
// migrating stored configs or breaking existing events.
//
// Default posture: everything OFF. The project-level master switch
// (`projects.autocapture_enabled`, default false) gates all of this regardless.
//
// Note: SPA route-change pageview detection is a CORE SDK behavior (governed by
// the SDK's own `enableAutoPageviews`), NOT an autocapture type — it is not
// represented here.

// Default file extensions treated as downloads (no PII — filename/extension
// only). Per-project overridable via `downloads.extensions`.
export const DEFAULT_DOWNLOAD_EXTENSIONS = [
  'pdf',
  'zip',
  'csv',
  'xlsx',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'dmg',
  'pkg',
  'mp3',
  'mp4',
  'wav',
] as const;

// Per-type capture toggles. All OFF by default. Each is independently
// toggleable per project. `notFound` is RESERVED for a future 404 / error-page
// detection feature — it stays in the schema (so stored configs and the SDK
// projection keep a stable shape) but is inert and undocumented for now.
export const autocaptureTypesSchema = z
  .object({
    outboundLinks: z.boolean().default(false),
    fileDownloads: z.boolean().default(false),
    formSubmissions: z.boolean().default(false),
    // Native <video>/<audio> engagement: play / pause / progress milestones
    // (25/50/75/100%) / complete, emitted as discrete events carrying metadata
    // only ({ src/id, title, percent, duration }) — NOT a recording, and
    // distinct from session replay. Embedded third-party players
    // (YouTube/Vimeo/Wistia) are a separate integration, NOT covered here.
    mediaPlayback: z.boolean().default(false),
    notFound: z.boolean().default(false),
  })
  .strict();
export type AutocaptureTypes = z.infer<typeof autocaptureTypesSchema>;

// 404 / error-page detection signal. Detection must be EXPLICITLY config-driven
// (title/path pattern or a page marker/selector), never inferred. Nullable +
// default null = no signal configured (404 capture effectively inert even if
// `types.notFound` is on).
export const autocaptureNotFoundSchema = z
  .object({
    titlePattern: z.string().optional(),
    pathPattern: z.string().optional(),
    selector: z.string().optional(),
  })
  .strict()
  .nullable()
  .default(null);
export type AutocaptureNotFound = z.infer<typeof autocaptureNotFoundSchema>;

// File-download capture config. `extensions` is the per-project list of
// extensions (lowercase, no leading dot) that mark an anchor href as a
// download.
export const autocaptureDownloadsSchema = z
  .object({
    extensions: z
      .array(z.string())
      .default([...DEFAULT_DOWNLOAD_EXTENSIONS]),
  })
  .strict();
export type AutocaptureDownloads = z.infer<typeof autocaptureDownloadsSchema>;

// Bump when the shape changes in a backward-incompatible way. Stored configs
// carry their own `version`; readers merge over the current defaults.
export const AUTOCAPTURE_SCHEMA_VERSION = 1;

export const autocaptureSettingsSchema = z
  .object({
    version: z.number().int().default(AUTOCAPTURE_SCHEMA_VERSION),
    // Function-form defaults: a missing sub-object resolves to the fully
    // defaulted sub-schema (zod v4 requires the input type for value-form
    // `.default()`, so we materialize the default via `.parse({})`).
    types: autocaptureTypesSchema.default(() => autocaptureTypesSchema.parse({})),
    notFound: autocaptureNotFoundSchema,
    downloads: autocaptureDownloadsSchema.default(() =>
      autocaptureDownloadsSchema.parse({}),
    ),
  })
  .strict();
export type AutocaptureSettings = z.infer<typeof autocaptureSettingsSchema>;

// The fully-defaulted settings object (everything off).
// Computed once from the schema so it never drifts from the field defaults.
export const DEFAULT_AUTOCAPTURE_SETTINGS: AutocaptureSettings =
  autocaptureSettingsSchema.parse({});

/**
 * Resolve stored jsonb into a fully-populated `AutocaptureSettings`, merging
 * over the schema defaults. Fails safe: any parse error (corrupt/legacy/foreign
 * shape) returns the defaults rather than throwing, so a bad stored config can
 * never break ingestion or the config projection.
 *
 * Note: `.strict()` rejects unknown keys, so a stored object carrying a future
 * field would fall back to defaults under the current schema — acceptable for
 * MVP since the only writer is the validated admin path. Versioning lets a
 * later schema accept those fields.
 */
export const resolveAutocaptureSettings = (
  stored: unknown,
): AutocaptureSettings => {
  if (stored == null) return DEFAULT_AUTOCAPTURE_SETTINGS;
  const parsed = autocaptureSettingsSchema.safeParse(stored);
  return parsed.success ? parsed.data : DEFAULT_AUTOCAPTURE_SETTINGS;
};
